/**
 * Texas retail billing (DESIGN §4.4).
 *
 * Replaces the ES CA NEM 3.0 rate engine with REP energy charge + base fee +
 * TDU pass-through on imports, and a solar buyback credit ledger
 * (`energy_only` | `entire_bill`, optional rollover). The ledger runs linearly
 * across a contract term of any length, so credits carry across months beyond
 * 12 (seasonal flows wrap by `(start + i) mod 12`).
 *
 * The Intelligent Octopus VPP credit is NOT part of any bill computed here.
 * Octopus keeps it; see `vppProgramValue` in `bid.ts`.
 *
 * All money values are unrounded USD. Round only for display ({@link roundCents}).
 *
 * Pure: no DOM, network or global state.
 */
import type { BuybackApplyMode } from "../config/types";
import { HOURS_IN_YEAR, MONTH_HOURS, MONTH_START_HOUR } from "./calendar";

// ── Energy flows ────────────────────────────────────────────────────────────

/** Monthly grid energy flows for one calendar month. */
export interface MonthlyFlows {
  /** Calendar month, 0 = January. */
  readonly month: number;
  /** Total grid imports incl. EV, kWh. */
  readonly importsKwh: number;
  /** EV kWh (all imported from the grid overnight), kWh. Included in `importsKwh`. */
  readonly evKwh: number;
  /** Solar exported to the grid, kWh. */
  readonly exportsKwh: number;
}

/**
 * Aggregates hourly vectors into 12 calendar-month flows:
 * `net(h) = home(h) − solar(h) − (discharge(h) − charge(h))`,
 * `imports = Σ max(0, net) + Σ ev`, `exports = Σ max(0, −net)`.
 * Pass `netHome` directly when a battery was simulated (its `netGrid`),
 * otherwise `home − solar` is used.
 */
export function aggregateFlows(args: {
  home: ArrayLike<number>;
  ev?: ArrayLike<number>;
  solar?: ArrayLike<number>;
  /** Battery-adjusted net home load; overrides `home − solar` when given. */
  netHome?: ArrayLike<number>;
}): MonthlyFlows[] {
  const { home, ev, solar, netHome } = args;
  for (const [name, v] of [["home", home], ["ev", ev], ["solar", solar], ["netHome", netHome]] as const) {
    if (v && v.length !== HOURS_IN_YEAR) throw new Error(`${name} must have ${HOURS_IN_YEAR} values, got ${v.length}`);
  }
  const out: MonthlyFlows[] = [];
  for (let m = 0; m < 12; m++) {
    let imp = 0;
    let exp = 0;
    let evk = 0;
    const start = MONTH_START_HOUR[m];
    const end = start + MONTH_HOURS[m];
    for (let h = start; h < end; h++) {
      const net = netHome ? netHome[h] : home[h] - (solar ? solar[h] : 0);
      if (net > 0) imp += net;
      else exp -= net;
      if (ev) evk += ev[h];
    }
    out.push({ month: m, importsKwh: imp + evk, evKwh: evk, exportsKwh: exp });
  }
  return out;
}

// ── Pricing ─────────────────────────────────────────────────────────────────

/** REP pricing for one bill computation. */
export interface RetailPricing {
  /** REP energy charge, $/kWh. For an all-in plan this already includes TDU. */
  readonly energyRate: number;
  /** $/kWh applied to EV kWh. Defaults to `energyRate`. */
  readonly evRate?: number;
  /** REP base fee, $/month. */
  readonly baseFee: number;
  /** True for all-in plans: TDU charges are already inside `energyRate`. */
  readonly tduIncluded: boolean;
}

/** TDU pass-through charges. */
export interface TduPricing {
  /** Customer + metering, $/month. */
  readonly fixedMonthly: number;
  /** $/kWh on every imported kWh. */
  readonly volumetricRate: number;
}

/** Solar buyback rules. `rate: 0` disables buyback. */
export interface BuybackRules {
  readonly rate: number;
  readonly applyMode: BuybackApplyMode;
  readonly rollover: boolean;
}

export const NO_BUYBACK: BuybackRules = { rate: 0, applyMode: "energy_only", rollover: false };

/** Line-item breakdown of one monthly bill. Money in USD (unrounded). */
export interface MonthlyBill {
  /** Calendar month, 0 = January. */
  readonly month: number;
  /** Position within the term, 0-based. */
  readonly termIndex: number;
  readonly importsKwh: number;
  readonly evKwh: number;
  readonly exportsKwh: number;
  /** `repRate × (imports − ev) + evRate × ev`. */
  readonly energyCharge: number;
  readonly tduFixed: number;
  /** `tduRate × imports`. */
  readonly tduVolumetric: number;
  readonly baseFee: number;
  /** `baseFee + energyCharge + tduFixed + tduVolumetric` (before credits). */
  readonly chargesBeforeCredits: number;
  /** Credit balance carried in from the previous month. */
  readonly creditCarriedIn: number;
  /** `buybackRate × exports`. */
  readonly buybackEarned: number;
  /** Credit applied against this bill. */
  readonly buybackApplied: number;
  /** Credit balance carried to the next month (0 without rollover). */
  readonly creditCarriedOut: number;
  /** Credit that expired unused this month (no rollover). */
  readonly creditForfeited: number;
  /** Amount the customer pays: `chargesBeforeCredits − buybackApplied`. */
  readonly total: number;
}

/** Bills for a contract term. */
export interface TermBills {
  readonly termMonths: number;
  /** Calendar month the term starts in, 0 = January. */
  readonly startMonth: number;
  /** One bill per term month, in order. `months[i].month === (startMonth + i) % 12`. */
  readonly months: MonthlyBill[];
  /** Σ `months[i].total`. */
  readonly total: number;
  /** `total / termMonths`. */
  readonly avgMonthly: number;
  /** Credit left at the end of the term. */
  readonly endingCredit: number;
}

/** Computes one month's bill and the next ledger balance (DESIGN §4.4). */
export function billMonth(
  flows: MonthlyFlows,
  retail: RetailPricing,
  tdu: TduPricing,
  buyback: BuybackRules,
  creditCarriedIn: number,
  termIndex = 0,
): MonthlyBill {
  const evRate = retail.evRate ?? retail.energyRate;
  const homeImports = Math.max(0, flows.importsKwh - flows.evKwh);
  const energyCharge = retail.energyRate * homeImports + evRate * flows.evKwh;
  const tduFixed = retail.tduIncluded ? 0 : tdu.fixedMonthly;
  const tduVolumetric = retail.tduIncluded ? 0 : tdu.volumetricRate * flows.importsKwh;
  const baseFee = retail.baseFee;
  const chargesBeforeCredits = baseFee + energyCharge + tduFixed + tduVolumetric;

  const buybackEarned = buyback.rate * flows.exportsKwh;
  const pool = creditCarriedIn + buybackEarned;
  const offsettable = buyback.applyMode === "entire_bill" ? chargesBeforeCredits : energyCharge;
  const buybackApplied = Math.min(pool, Math.max(0, offsettable));
  const remaining = pool - buybackApplied;
  const creditCarriedOut = buyback.rollover ? remaining : 0;

  return {
    month: flows.month,
    termIndex,
    importsKwh: flows.importsKwh,
    evKwh: flows.evKwh,
    exportsKwh: flows.exportsKwh,
    energyCharge,
    tduFixed,
    tduVolumetric,
    baseFee,
    chargesBeforeCredits,
    creditCarriedIn,
    buybackEarned,
    buybackApplied,
    creditCarriedOut,
    creditForfeited: buyback.rollover ? 0 : remaining,
    total: chargesBeforeCredits - buybackApplied,
  };
}

/**
 * Bills a contract term: for i < termMonths, bills calendar month
 * `(startMonth + i) mod 12` with the credit ledger carried linearly across
 * the whole term (an 18-month term starting in October covers two winters).
 */
export function billTerm(
  flows12: readonly MonthlyFlows[],
  retail: RetailPricing,
  tdu: TduPricing,
  buyback: BuybackRules,
  startMonth: number,
  termMonths: number,
  openingCredit = 0,
): TermBills {
  if (flows12.length !== 12) throw new Error(`flows12 must have 12 months, got ${flows12.length}`);
  if (!Number.isInteger(startMonth) || startMonth < 0 || startMonth > 11) {
    throw new RangeError(`startMonth must be an integer 0–11, got ${startMonth}`);
  }
  if (!Number.isInteger(termMonths) || termMonths < 1) throw new RangeError(`termMonths must be ≥ 1, got ${termMonths}`);
  const months: MonthlyBill[] = [];
  let credit = openingCredit;
  let total = 0;
  for (let i = 0; i < termMonths; i++) {
    const m = (startMonth + i) % 12;
    const f = flows12[m]!;
    if (f.month !== m) throw new Error(`flows12[${m}] has month ${f.month}`);
    const bill = billMonth(f, retail, tdu, buyback, credit, i);
    months.push(bill);
    credit = bill.creditCarriedOut;
    total += bill.total;
  }
  return { termMonths, startMonth, months, total, avgMonthly: total / termMonths, endingCredit: credit };
}

/** Rounds USD to cents (half away from zero, robust to binary float error). */
export function roundCents(x: number): number {
  const s = Math.sign(x);
  return (s * Math.round(Number((Math.abs(x) * 100).toFixed(6)))) / 100;
}
